<template lang="pug">
.send-message-detail
  .contents.d-flex.flex-row
    .send-message
      SendMessageDetailContent(v-model="message" :readCount="readCount" :unreadCount="unreadCount"
        @onSendTargetList="onSendTargetList" @onPreview="onPreview" @onRead="onRead" @onUnread="onUnread")
    .reply-messages
      .reply-list(v-show="showReplyList")
        SendMessageReplyListTable(v-model="replyItems" :fields="replyFields" :isLoading="replyLoading" :type="0" @onMessage="onMessage" @onReadSwitch="onReadSwitch")
      .reply-detail(v-show="showReplyDetail")
        SendMessageReplyDetailBox(v-model="replyDetailModalText" :message="replyDetailModalMessage" @onReply="onReply" @onBack="onBack")

  SendMessageFilePreviewModal(ref="previewModal")
  SendMessageReadFamiliesModal(v-model="showSendMessageReadFamiliesModal" :families="sendMessageReadFamilies" :showRoomName="false"
    residentNameColumnName="入所申込者名" :csvTypeName="sendMessageCsvTypeName" :sendMessageTitle="sendMessageTitle")

  OkCancelModal(v-model="showSendMessageModal" title="メッセージを送信しますか？" @ok="registerReplyMessage(replyTargetItem)")
  OkModal(v-model="showSendMessageSuccessModal" title="メッセージ送信を受け付けました。" @ok="onReplySuccessOk")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Prop, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import Setting from "../../../../public/setting.json";
import S3UrlService from "@/commons/services/S3Url";
import WaiterSendMessageReadCountsService, { WaiterSendMessageReadCountsModel } from "@/commons/services/WaiterSendMessageReadCounts";
import SendMessageReadFamiliesService, { SendMessageReadFamilyModel } from "@/commons/services/SendMessageReadFamilies";
import SendMessageRepliesService, { SendMessageRepliesPostModel, SendMessageReplyModel, SendMessageReplySenderType } from "@/commons/services/SendMessageReplies";
import SendMessageRepliesFamiliesService, { SendMessageRepliesFamilyPatchModel } from "@/commons/services/SendMessageRepliesFamilies";
import FamiliesService, { FamilyGetModel } from "@/commons/services/Families";

import PageLoading from "@/pc/basics/PageLoading.vue";
import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import SendMessageFilePreviewModal from "@/pc/components/modals/SendMessageFilePreviewModal.vue";
import SendMessageDetailContent from "@/pc/containers/sendMessages/SendMessageDetailContent.vue";
import SendMessageReplyListTable, {
  SendMessageReplyListTableItem,
  SendMessageReplyListTableField,
  SendMessageReplyListTableType
} from "@/pc/containers/sendMessages/SendMessageReplyListTable.vue";
import SendMessageReplyDetailBox from "@/pc/containers/sendMessages/SendMessageReplyDetailBox.vue";

import SendMessageReadFamiliesModal from "@/pc/components/modals/SendMessageReadFamiliesModal.vue";
import { SendMessageHistoryTableItem } from "@/pc/containers/sendMessages/SendMessageHistoryTable.vue";
import { SenderType } from "@/commons/models/Resident";

/**
 * メッセージ送信詳細コンポーネント
 */
@Component({
  components: {
    PageLoading,
    OkCancelModal,
    OkModal,
    SendMessageFilePreviewModal,
    SendMessageDetailContent,
    SendMessageReplyListTable,
    SendMessageReadFamiliesModal,
    SendMessageReplyDetailBox
  }
})
export default class MessageDetailTab extends Vue {
  /** 送信メッセージにひもづく家族サービス */
  @Inject("SendMessageReadFamiliesService") sendMessageReadFamiliesService!: SendMessageReadFamiliesService;
  /** メッセージ(既読/未読)サービス */
  @Inject("WaiterSendMessageReadCountsService") waiterSendMessageReadCounts!: WaiterSendMessageReadCountsService;
  /** S3Urlサービス */
  @Inject("S3UrlService") s3UrlService!: S3UrlService;
  /** メッセージ送信の返信サービス */
  @Inject("SendMessageRepliesService") sendMessageRepliesService!: SendMessageRepliesService;
  /** メッセージ送信の返信サービス(家族) */
  @Inject("SendMessageRepliesFamiliesService") sendMessageRepliesFamiliesService!: SendMessageRepliesFamiliesService;
  /** 家族ユーザーサービス */
  @Inject("FamiliesService") familiesService!: FamiliesService;

  /** 表示対象のメッセージ */
  @Prop({
    required: true
  })
  message!: SendMessageHistoryTableItem;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** メッセージ送信確認モーダル表示可否 */
  showSendMessageModal: boolean = false;
  /** メッセージ送信完了モーダル表示可否 */
  showSendMessageSuccessModal: boolean = false;

  /** 返信リストの表示可否 */
  showReplyList: boolean = true;
  /** 返信列名 */
  replyFields: Array<SendMessageReplyListTableField> = [{ key: "message", label: "", sortable: false, sortDirection: "desc", class: "message" }];
  /** 返信メッセージ */
  replyItems: Array<SendMessageReplyListTableItem> = [];
  /** 返信のローディング表示 */
  replyLoading: boolean = true;

  /** 返信メッセージの詳細・返信の表示可否 */
  showReplyDetail: boolean = false;
  /** 返信メッセージの詳細・返信で入力する返信内容 */
  replyDetailModalText: string | null = null;
  /** 返信メッセージの詳細・返信で表示するメッセージ */
  replyDetailModalMessage: SendMessageReplyListTableItem | null = null;
  /** 返信メッセージ送信対象のメッセージ */
  replyTargetItem: SendMessageReplyListTableItem | null = null;

  /** 送信メッセージの既読数 */
  readCount: number = -1;
  /** 送信メッセージの未読数 */
  unreadCount: number = -1;

  /** 既読／未読家族ユーザーモーダル表示可否 */
  showSendMessageReadFamiliesModal: boolean = false;
  /** 既読／未読家族ユーザーモーダルに渡す家族 */
  sendMessageReadFamilies: Array<SendMessageReadFamilyModel> = [];
  /** 既読／未読家族どちらか */
  sendMessageCsvTypeName: string = "";
  /** CSV出力時に使用するメッセージの件名 */
  sendMessageTitle: string = "";

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    window.setTimeout(async () => {
      await this.createView();
    }, 2000);

    return;
  }

  /**
   * ### 画面表示情報を作成する
   */
  public async createView(): Promise<void> {
    this.replyLoading = true;

    // 送信メッセージの既読/未読数を取得する
    const readModel: WaiterSendMessageReadCountsModel = await this.getSendMessageRead();
    this.readCount = readModel.readCounts;
    this.unreadCount = readModel.unreadCounts;

    // 返信リストに表示する返信を取得する
    const replyMessages: Array<SendMessageReplyModel> = await this.getSendMessageReplies(Number(this.message.datetime));
    const replyItems: Array<SendMessageReplyListTableItem> = this.createReplyItems(replyMessages);
    this.replyItems = replyItems;
    this.replyLoading = false;

    return;
  }

  /**
   * ### 送信メッセージの既読/未読数を取得する
   */
  public async getSendMessageRead(): Promise<WaiterSendMessageReadCountsModel> {
    const message: SendMessageHistoryTableItem = this.message;
    const readModel: WaiterSendMessageReadCountsModel = await this.waiterSendMessageReadCounts.get(userStore, Number(message.datetime));
    console.debug("getSendMessageRead() Number(message.datetime):", Number(message.datetime));
    return readModel;
  }

  /**
   * ### メッセージの返信を取得する
   */
  public async getSendMessageReplies(messageDatetime: number): Promise<Array<SendMessageReplyModel>> {
    const response: Array<SendMessageReplyModel> = await await this.sendMessageRepliesService.get(userStore, messageDatetime);
    return response;
  }

  /**
   * ### 返信リストの行情報を作成する
   */
  public createReplyItems(replies: Array<SendMessageReplyModel>): Array<SendMessageReplyListTableItem> {
    // 返信メッセージの1行はユーザーの1番古いデータになるので、datetimeの古い順にソートする
    const sortedReplies: Array<SendMessageReplyModel> = replies.sort((a, b) => a.datetime - b.datetime);

    // ユーザー単位でメッセージをMAP化する
    let map: Map<string, Array<SendMessageReplyModel>> = new Map<string, Array<SendMessageReplyModel>>();
    for (const reply of sortedReplies) {
      const messages: Array<SendMessageReplyModel> | undefined = map.get(`${reply.residentId}_${reply.familyId}`);
      if (messages) {
        messages.push(reply);
        map.set(`${reply.residentId}_${reply.familyId}`, messages);
      } else {
        map.set(`${reply.residentId}_${reply.familyId}`, [reply]);
      }
    }

    // MAP化したものを行情報にする
    let items: Array<SendMessageReplyListTableItem> = [];
    map.forEach((value: Array<SendMessageReplyModel>) => {
      const sortedValue: Array<SendMessageReplyModel> = value.sort((a, b) => a.datetime - b.datetime);

      // 行の並び順は最後のメッセージの日付の新しいもの順にしたいので、行情報は最新のものを使用する
      const index: number = sortedValue.length - 1;

      // 未読かどうかは家族の返信で未読が存在するか否かで判断する
      let readed: boolean = true;
      for (const value of sortedValue) {
        if (value.senderType == SendMessageReplySenderType.FAMILY && value.readUserIds.indexOf(userStore.loginUser.userId) < 0) {
          readed = false;
          break;
        }
      }

      const subName: string = sortedValue[index].residentRoom
        ? `${sortedValue[index].residentRoom}・${sortedValue[index].residentName}・${sortedValue[index].familyRelation}`
        : `${sortedValue[index].residentName}・${sortedValue[index].familyRelation}`;

      items.push({
        parentMessagesSortKey: sortedValue[index].parentMessagesSortKey,
        residentId: sortedValue[index].residentId,
        familyId: sortedValue[index].familyId,
        mainName: sortedValue[index].familyName,
        subName: subName,
        datetime: sortedValue[index].datetime,
        readed: readed,
        type: sortedValue[index].type as SendMessageReplyListTableType,
        messages: value,
        filterKey1: sortedValue[index].residentName,
        filterKey2: sortedValue[index].residentRoom,
        filterKey3: sortedValue[index].familyName
      });
    });

    // 画面表示では日付の新しい順なのでソートしなおす
    const sortedItems: Array<SendMessageReplyListTableItem> = items.concat().sort((a, b) => b.datetime - a.datetime);

    return sortedItems;
  }

  /**
   * ### 宛先リストを表示する
   */
  public async onSendTargetList(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const families: Array<SendMessageReadFamilyModel> = await this.getSendMessageReadFamilies();
    this.sendMessageReadFamilies = families;

    this.isBusy = false;

    this.showSendMessageReadFamiliesModal = true;

    return;
  }

  /**
   * ### プレビューボタン押下イベント処理
   *
   * - ダイアログで送信メッセージの添付ファイルをプレビュー表示する
   */
  public async onPreview(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const contents: any = this.message.functionContents;
    let files: Array<any> = [];
    if (contents.fileUrls) {
      for (let fileUrl of contents.fileUrls) {
        const file: File = await this.getS3File(fileUrl);
        files.push(file);
      }
    }

    const modal: SendMessageFilePreviewModal | Vue | Element | Vue[] | Element[] = this.$refs.previewModal;
    if (modal instanceof SendMessageFilePreviewModal) {
      modal.show("", false, files);
    }

    this.isBusy = false;

    return;
  }

  /**
   * ### S3に格納されているFileを取得する
   *
   * @param url 元URL
   */
  public async getS3File(url: string): Promise<File> {
    const response: File = await this.s3UrlService.getFile(userStore, Setting.backets.imageExpireHour, url);

    return response;
  }

  /**
   * ### 返信メッセージを既読にする
   */
  public async onMessage(item: SendMessageReplyListTableItem): Promise<void> {
    const family: FamilyGetModel | null = await this.getFamily(item.residentId as number, item.familyId as number);
    item.isDeleted = family == null ? true : false;

    this.replyDetailModalMessage = item;
    this.replyDetailModalText = "";
    this.showReplyList = false;
    this.showReplyDetail = true;

    for (const message of item.messages as Array<SendMessageReplyModel>) {
      if (message.readUserIds.indexOf(userStore.loginUser.userId) < 0 ? true : false) {
        const request: SendMessageRepliesFamilyPatchModel = {
          readUserId: userStore.loginUser.userId,
          readUserName: userStore.loginUser.name,
          read: true
        };

        await this.sendMessageRepliesFamiliesService.patch(userStore, Number(this.message.datetime), message.residentId, message.familyId, message.datetime, request);

        message.readUserIds.push(userStore.loginUser.userId);
        item.readed = true;
      }
    }

    return;
  }

  /**
   * ### 家族を取得する
   *
   * - 家族単一を取得するAPIはないので、利用者にひもづく家族を取得する
   * - 家族の中から家族IDが同一のものを修正対象の家族と判断する
   */
  public async getFamily(residentId: number, familyId: number): Promise<FamilyGetModel | null> {
    const families: Array<FamilyGetModel> = await this.familiesService.getsInResident(userStore, residentId);

    for (const family of families) {
      if (family.familyId == familyId) {
        return family;
      }
    }
    return null;
  }

  public onReply(item: SendMessageReplyListTableItem): void {
    this.showSendMessageModal = true;
    this.replyTargetItem = item;
    return;
  }

  public async registerReplyMessage(item: SendMessageReplyListTableItem): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "送信中...";

    const requestBody: SendMessageRepliesPostModel = {
      message: this.replyDetailModalText as string
    };

    // 送信種別に応じて呼び出すサービスを切り替える
    if (this.message.senderType == SenderType.LINE) {
      await this.sendMessageRepliesService.post(userStore, item.parentMessagesSortKey, item.residentId as number, item.familyId as number, requestBody);
    } else {
      await this.sendMessageRepliesService.postEMail(userStore, item.parentMessagesSortKey, item.residentId as number, item.familyId as number, requestBody);
    }

    this.isBusy = false;

    // メッセージ送信成功ダイアログを表示する
    this.showSendMessageSuccessModal = true;

    return;
  }

  public async onReplySuccessOk(): Promise<void> {
    await this.createView();

    const selectedItem: SendMessageReplyListTableItem = this.replyDetailModalMessage as SendMessageReplyListTableItem;

    for (const item of this.replyItems) {
      if (item.residentId == selectedItem.residentId && item.familyId == selectedItem.familyId) {
        this.replyDetailModalMessage = item;
        this.replyDetailModalText = "";
      }
    }

    return;
  }

  public onBack(): void {
    this.showReplyDetail = false;
    this.showReplyList = true;
    return;
  }

  /**
   * ### 既読リストを表示する
   */
  public async onRead(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const families: Array<SendMessageReadFamilyModel> = await this.getSendMessageReadFamilies();
    const unreadFamilies: Array<SendMessageReadFamilyModel> = await this.createRreadFamilies(families);
    this.sendMessageCsvTypeName = "既読一覧";
    this.sendMessageTitle = this.message.functionContents.title;
    this.sendMessageReadFamilies = unreadFamilies;

    this.isBusy = false;

    this.showSendMessageReadFamiliesModal = true;

    return;
  }

  /**
   * ### 未読リストを表示する
   */
  public async onUnread(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const families: Array<SendMessageReadFamilyModel> = await this.getSendMessageReadFamilies();
    const unreadFamilies: Array<SendMessageReadFamilyModel> = await this.createUnreadFamilies(families);
    this.sendMessageCsvTypeName = "未読一覧";
    this.sendMessageTitle = this.message.functionContents.title;
    this.sendMessageReadFamilies = unreadFamilies;

    this.isBusy = false;

    this.showSendMessageReadFamiliesModal = true;

    return;
  }

  /**
   * ### メッセージ送信したメッセージにひもづく家族を取得する
   */
  public async getSendMessageReadFamilies(): Promise<Array<SendMessageReadFamilyModel>> {
    const families: Array<SendMessageReadFamilyModel> = await this.sendMessageReadFamiliesService.get(userStore, Number(this.message.datetime));

    return families;
  }

  /**
   * ### メッセージ送信したメッセージにひもづく家族から既読の家族を返却する
   */
  public createRreadFamilies(families: Array<SendMessageReadFamilyModel>): Array<SendMessageReadFamilyModel> {
    let unreadFamilies: Array<SendMessageReadFamilyModel> = [];

    for (let family of families) {
      if (family.readDatetime != undefined || family.readDatetime != null) {
        unreadFamilies.push(family);
      }
    }

    return unreadFamilies;
  }

  /**
   * ### メッセージ送信したメッセージにひもづく家族から未読の家族を返却する
   */
  public createUnreadFamilies(families: Array<SendMessageReadFamilyModel>): Array<SendMessageReadFamilyModel> {
    let unreadFamilies: Array<SendMessageReadFamilyModel> = [];

    for (let family of families) {
      if (family.readDatetime == undefined || family.readDatetime == null) {
        unreadFamilies.push(family);
      }
    }

    return unreadFamilies;
  }

  /**
   * ### 既読／未読の切り替え
   */
  public async onReadSwitch(item: SendMessageReplyListTableItem): Promise<void> {
    // ローディングを表示して操作不可にしないと連打で複数かい更新が走る
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    for (const message of item.messages as Array<SendMessageReplyModel>) {
      // 変更後の値
      const afterRead: boolean = !item.readed;
      // readUserIdsに自分のユーザーIDが存在すれば現在は「既読」、存在しなければ現在は「未読」
      const currentRead: boolean = -1 < message.readUserIds.indexOf(userStore.loginUser.userId) ? true : false;
      // 変更後の値と現在の値が異なる場合のみ更新が必要
      // 例：2件返信があり、1件目は既読、2件目は未読の場合は画面上「未読」として扱っている場合、2件目だけを「既読」にしないと同じuserIdが複数登録されてしまう
      if (afterRead != currentRead) {
        const request: SendMessageRepliesFamilyPatchModel = {
          readUserId: userStore.loginUser.userId,
          readUserName: userStore.loginUser.name,
          read: afterRead
        };

        await this.sendMessageRepliesFamiliesService.patch(userStore, Number(this.message.datetime), message.residentId, message.familyId, message.datetime, request);

        if (afterRead) {
          message.readUserIds.push(userStore.loginUser.userId);
        } else {
          const index: number = message.readUserIds.indexOf(userStore.loginUser.userId);
          message.readUserIds.splice(index, 1);
        }
      }
    }
    // 最後に既読／未読を反転させる
    item.readed = !item.readed;

    this.isBusy = false;

    return;
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";
@import "../../../styles/pc/_mixin.scss";

.send-message-detail {
  .contents {
    height: calc(100vh - 224px);
    max-width: 1280px;
    overflow-x: auto;

    .send-message,
    .reply-messages {
      padding-top: 20px;
    }
    .send-message {
      width: 520px;
      min-width: 520px;
    }
    .reply-messages {
      width: 754px;
      min-width: 440px;
    }
  }
}
</style>
